import React from "react";
import { FiEye, FiFilter, FiGrid, FiList, FiPlus, FiX } from "react-icons/fi";
import { PageRenderer, RendererRuntimeProvider, type All8PageContent } from "@apa/core-renderers";
import { Modal } from "../Modal";
import type { ComponentDefinition } from "../../Pages/pageComponentLibrary";

type LibraryView = "grid" | "list" | "visual";

type PageEditorComponentLibraryModalProps = {
	show: boolean;
	onClose: () => void;
	libraryView: LibraryView;
	setLibraryView: (view: LibraryView) => void;
	componentSearch: string;
	setComponentSearch: (value: string) => void;
	filteredLibrary: ComponentDefinition[];
	availableTags: string[];
	activeTags: string[];
	onToggleTag: (tag: string) => void;
	onClearTags: () => void;
	librarySelection: ComponentDefinition | null;
	setLibrarySelection: (definition: ComponentDefinition | null) => void;
	addComponentFromDefinition: (definition: ComponentDefinition | null) => void;
	runtimeConfig: any;
};

/** Component library modal with grid, list and visual views for adding blocks to the page. */
const PageEditorComponentLibraryModal: React.FC<PageEditorComponentLibraryModalProps> = ({
	show,
	onClose,
	libraryView,
	setLibraryView,
	componentSearch,
	setComponentSearch,
	filteredLibrary,
	availableTags,
	activeTags,
	onToggleTag,
	onClearTags,
	librarySelection,
	setLibrarySelection,
	addComponentFromDefinition,
	runtimeConfig
}) => {
	const tagLabelMap: Record<string, string> = {
		dynamic: "Dynamic container",
		card: "Card",
		cards: "Card",
		form: "Form",
		image: "Image",
		header: "Header",
		grid: "Grid"
	};
	const formatTagLabel = (tag: string) => tagLabelMap[tag] ?? tag.replace(/-/g, " ");
	const hasImageTag = availableTags.includes("image");
	const hasDynamicTag = availableTags.includes("dynamic");
	const mainTags = ["grid", "header", "card", "form"];
	const otherTags = availableTags.filter(
		(tag) => mainTags.includes(tag) && tag !== "image" && tag !== "dynamic"
	);
	const activeCount = activeTags.length;

	return (
		<Modal show={show} onHide={onClose} size="full85" centered>
			<Modal.Header closeButton className="rounded-t-xl border-none bg-gradient-to-r from-white via-slate-50 to-slate-100 px-6 py-5">
				<div className="space-y-1">
					<Modal.Title className="text-xl font-semibold text-slate-900">Component library</Modal.Title>
					<p className="text-sm text-slate-500">Pick a block, review its visual preview, and drop it into the page.</p>
				</div>
				<div className="flex items-center gap-2">
					{hasImageTag && (
						<label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
							<input
								type="checkbox"
								className="h-3.5 w-3.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
								checked={activeTags.includes("image")}
								onChange={() => onToggleTag("image")}
							/>
							<span>Image</span>
						</label>
					)}
					<label
						className={`inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 ${
							hasDynamicTag ? "" : "opacity-50"
						}`}
					>
						<input
							type="checkbox"
							disabled={!hasDynamicTag}
							className="h-3.5 w-3.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
							checked={activeTags.includes("dynamic")}
							onChange={() => onToggleTag("dynamic")}
						/>
						<span>Dynamic container</span>
					</label>
					<details className="relative">
						<summary className="list-none inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:border-slate-300">
							<FiFilter /> Filters
							{activeCount > 0 && (
								<span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold text-white">
									{activeCount}
								</span>
							)}
						</summary>
						<div className="absolute right-0 z-20 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-3 shadow-lg">
							<div className="flex items-center justify-between">
								<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Filter by tag</p>
								<button
									type="button"
									onClick={onClearTags}
									disabled={activeCount === 0}
									className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
								>
									<FiX /> Clear
								</button>
							</div>
							{otherTags.length === 0 ? (
								<p className="mt-3 text-xs text-slate-500">No tags available.</p>
							) : (
								<div className="mt-3 grid gap-2">
									{otherTags.map((tag) => (
										<label key={tag} className="inline-flex items-center gap-2 text-xs text-slate-700">
											<input
												type="checkbox"
												className="h-3.5 w-3.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
												checked={activeTags.includes(tag)}
												onChange={() => onToggleTag(tag)}
											/>
											<span>{formatTagLabel(tag)}</span>
										</label>
									))}
								</div>
							)}
						</div>
					</details>
					<button
						type="button"
						onClick={() => setLibraryView("grid")}
						className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition ${
							libraryView === "grid"
								? "bg-slate-900 text-white shadow-sm"
								: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
						}`}
					>
						<FiGrid /> Grid
					</button>
					<button
						type="button"
						onClick={() => setLibraryView("list")}
						className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition ${
							libraryView === "list"
								? "bg-slate-900 text-white shadow-sm"
								: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
						}`}
					>
						<FiList /> List
					</button>
					<button
						type="button"
						onClick={() => setLibraryView("visual")}
						className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition ${
							libraryView === "visual"
								? "bg-slate-900 text-white shadow-sm"
								: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
						}`}
					>
						<FiEye /> Visual
					</button>
				</div>
			</Modal.Header>
			<Modal.Body className="bg-slate-50/80">
				<div
					className={`flex flex-col gap-4 ${
						libraryView === "visual" ? "lg:flex-row lg:items-start" : "lg:flex-col"
					}`}
				>
					<div className="space-y-3 lg:flex-1 lg:max-h-[70vh] lg:overflow-y-auto pr-1">
						<input
							className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
							placeholder="Search by name, tag, or description"
							value={componentSearch}
							onChange={(e) => setComponentSearch(e.target.value)}
						/>

					{libraryView === "grid" && (
						<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
							{filteredLibrary.map((definition) => {
								const isSelected = librarySelection?.type === definition.type;
								return (
									<button
										type="button"
										key={definition.type}
										onClick={() => setLibrarySelection(definition)}
										onDoubleClick={() => addComponentFromDefinition(definition)}
										className={`h-full rounded-xl border px-3 py-3 text-left transition ${
											isSelected
												? "border-slate-800 bg-white shadow-sm"
												: "border-slate-200 bg-white hover:border-slate-300"
										}`}
									>
										<p className="text-sm font-semibold text-slate-900">{definition.title}</p>
										<p className="text-xs text-slate-600 line-clamp-3">{definition.description}</p>
									</button>
								);
							})}
						</div>
					)}

					{libraryView === "list" && (
						<div className="space-y-2">
							{filteredLibrary.map((definition) => {
								const isSelected = librarySelection?.type === definition.type;
								return (
									<button
										type="button"
										key={definition.type}
										onClick={() => setLibrarySelection(definition)}
										onDoubleClick={() => addComponentFromDefinition(definition)}
										className={`w-full rounded-xl border px-4 py-3 text-left transition ${
											isSelected
												? "border-slate-800 bg-white shadow-sm"
												: "border-slate-200 bg-white hover:border-slate-300"
										}`}
									>
										<p className="text-sm font-semibold text-slate-900">{definition.title}</p>
										<p className="text-xs text-slate-600 line-clamp-2">{definition.description}</p>
										<div className="mt-2 flex flex-wrap gap-1">
											{(definition.tags ?? []).map((tag) => (
												<span
													key={`${definition.type}-${tag}`}
													className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
												>
													{tag}
												</span>
											))}
										</div>
									</button>
								);
							})}
						</div>
					)}

					{libraryView === "visual" && (
						<div className="space-y-2">
							{filteredLibrary.map((definition) => {
								const isSelected = librarySelection?.type === definition.type;
								return (
									<button
										type="button"
										key={definition.type}
										onClick={() => setLibrarySelection(definition)}
										onDoubleClick={() => addComponentFromDefinition(definition)}
										className={`w-full rounded-xl border px-4 py-3 text-left transition ${
											isSelected
												? "border-slate-800 bg-white shadow-sm"
												: "border-slate-200 bg-white hover:border-slate-300"
										}`}
									>
										<p className="text-sm font-semibold text-slate-900">{definition.title}</p>
										<p className="text-xs text-slate-600 line-clamp-2">{definition.description}</p>
									</button>
								);
							})}
						</div>
					)}
				</div>

				{libraryView === "visual" && (
					<div className="lg:w-1/2 xl:w-[55%] flex-shrink-0">
						{librarySelection ? (
							<div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
								<div className="border-b border-slate-100 px-4 py-3">
									<p className="text-sm font-semibold text-slate-900">{librarySelection.title}</p>
									<p className="text-sm text-slate-600">{librarySelection.description}</p>
								</div>
								<div className="p-4 space-y-4">
									<div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
										<RendererRuntimeProvider value={runtimeConfig}>
											<PageRenderer
												components={
													librarySelection.previewComponents ??
													([
														{
															componentType: librarySelection.type,
															key: "preview",
															content: JSON.parse(JSON.stringify(librarySelection.defaultContent))
														}
													] as All8PageContent)
												}
											/>
										</RendererRuntimeProvider>
									</div>
									<div className="flex justify-end">
										<button
											type="button"
											onClick={() => addComponentFromDefinition(librarySelection)}
											className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
										>
											<FiPlus /> Add to page
										</button>
									</div>
								</div>
							</div>
						) : null}
					</div>
				)}
			</div>
		</Modal.Body>
	</Modal>
	);
};

export default PageEditorComponentLibraryModal;
